/**
 * La formule de performance — SPEC-MOTEUR §4.
 *
 * ```
 * Facteur_effectif = Plancher + (Score mini-jeu / 100) × (Potentiel − Plancher)
 * VuesPic = (Découverte + Abonnés × Portée) × Accroche_eff × Qualité_eff
 *           × Diffusion_eff × Chance × Momentum
 * ```
 *
 * L'arbre et le matériel fixent le **potentiel**, le score d'Atelier décide de la
 * **capture**, la chance fait le reste (PLAN §4.4).
 *
 * ⚠️ Les ancres de `__tests__/ancres.test.ts` sont les chiffres réels de Mathieu : elles
 * font foi. Une retouche de formule qui les casse est un bug, pas un réglage (CLAUDE.md).
 */
// ─────────────────────────────────────────────────────────────────────────────
// §4.3 Planchers, et §3.7 de SPEC-CONTENU pour les plafonds de potentiel
// ─────────────────────────────────────────────────────────────────────────────
export const PLANCHER_ACCROCHE = 0.05;
export const PLANCHER_QUALITE = 0.3;
export const PLANCHER_DIFFUSION = 0.6;
/** Plafonds des potentiels (SPEC-CONTENU §3.7 — `potential_add` est borné par le moteur). */
export const PLAFOND_ACCROCHE = 2.8;
export const PLAFOND_QUALITE = 2.0;
export const PLAFOND_DIFFUSION = 1.5;
export const PLAFOND_PORTEE = 0.35;
export const PLANCHER_PORTEE = 0.1;
/**
 * ⟦A-17⟧ **Plafond de Portée TOTAL — 0,40.** La formule de base ne peut pas dépasser 0,35
 * (`0,1 + 0,2 + 0,05` à Fidélité 100 et momentum 1,5) : le clamp ci-dessus est donc son
 * maximum naturel, et le relever seul ne changerait rien. Ce qui change, c'est qu'un bonus
 * peut désormais s'ajouter **après** ce clamp, dans la marge de 0,05 que l'avenant ouvre.
 *
 * A-17 tranche ce point pour la **Hype** (SPEC-FIL, pas encore écrite) — *« la seule sortie
 * qui ne déplace aucune ancre »*. Le premier bénéficiaire est le `potential_add` de facteur
 * `portee` du nœud *Murmureur d'algorithme* (+0,05), agrégé par `effects.ts` depuis toujours
 * et **lu nulle part** : appliqué avant le clamp, il ne servait qu'aux joueurs sous 0,35,
 * c'est-à-dire à personne en fin de partie. La Hype s'y branchera de la même façon.
 */
export const PLAFOND_PORTEE_TOTAL = 0.4;
/** §4.3 — modificateurs de Qualité_eff APRÈS capture. Cumulables. */
export const MULT_CRUNCH = 0.7; // énergie < 20 (PLAN §4.2)
export const MULT_FATIGUE = 0.9; // SM ∈ [30;59] (PLAN §4.10)
/** §4.5 / §7.2 — modificateurs de VuesPic. */
export const MULT_VUES_CHAOS = 1.2; // vidéo chaos : « vues +20 % » (PLAN §4.7)
export const MULT_VUES_WINDOW = 3; // fenêtre d'opportunité (arbitrage A22)
/**
 * Poids volatil (§4.6, PLAN §4.4) : un short OU une vidéo chaos, publiée sous 50 de
 * Fidélité, ne convertit qu'à moitié — « ce public-là ne reste pas ».
 */
export const MULT_POIDS_VOLATIL = 0.5;
/** §4.4 — modificateur d'alignement du potentiel d'Accroche. */
export const ACCROCHE_BONUS_CHAOS = 0.3;
export const ACCROCHE_MALUS_LUMIERE = -0.1;
function clamp(v, min, max) {
    return Math.min(max, Math.max(min, v));
}
/** Capture d'un facteur : `Plancher + (Score/100) × (Potentiel − Plancher)` (§4.1). */
export function capture(plancher, potentiel, score) {
    return plancher + (clamp(score, 0, 100) / 100) * (potentiel - plancher);
}
// ─────────────────────────────────────────────────────────────────────────────
// §4.2 Mapping mini-jeu → facteur et score d'exécution
// ─────────────────────────────────────────────────────────────────────────────
/** `S_qual = 0,30·ecriture + 0,30·tournage + 0,40·montage` (PLAN §4.4). */
export function scoreQualite(s) {
    return 0.3 * s.ecriture + 0.3 * s.tournage + 0.4 * s.montage;
}
/**
 * Score d'exécution global (§4.2) — arbitrage **A1** : les poids du MOTEUR font foi.
 * - Longue : 20/20/25/20/15 · Short : 30/30/40
 */
export function executionScore(s, format) {
    if (format === "short")
        return scoreQualite(s);
    return (0.2 * s.ecriture +
        0.2 * s.tournage +
        0.25 * s.montage +
        0.2 * (s.miniature ?? 0) +
        0.15 * (s.promotion ?? 0));
}
/**
 * Score qui pilote l'Accroche — arbitrage **A2** : `S_acc = miniature` pour une longue,
 * et pour un short (pas d'étape miniature) `S_acc = execution_score`.
 */
export function scoreAccroche(s, format) {
    return format === "short" ? executionScore(s, format) : (s.miniature ?? 0);
}
// ─────────────────────────────────────────────────────────────────────────────
// §4.5 Découverte, Portée
// ─────────────────────────────────────────────────────────────────────────────
/**
 * `Découverte(A) = 30 + 270 × min(1 ; 0,5^((A − 1000) / 2000))` (§4.5).
 * → 300 pour A ≤ 1 000 ; ≈ 152 à 3 300 ; ≈ 42 à 10 000 ; → 30 (plancher).
 */
export function decouverte(abonnes) {
    return 30 + 270 * Math.min(1, Math.pow(0.5, (abonnes - 1000) / 2000));
}
/**
 * ⟦A-28⟧ **La recommandation algorithmique — le terme qui manquait pour aller au million.**
 *
 * ── Le défaut, calculé puis mesuré ────────────────────────────────────────────
 *
 * Le taux de croissance **par vidéo** vaut `subs_gagnés / A`, c'est-à-dire
 * `(Découverte(A)/A + Portée) × facteurs × TauxConversion`. Pour un gros A, `Découverte(A)/A`
 * tend vers 0 et il ne reste que `Portée × facteurs × TC` — or **les deux sont bornés par des
 * constantes** (`PLAFOND_PORTEE` = 0,35 ; conversion plafonnée à 0,006). Le taux **se fige et
 * n'en bouge plus jamais** :
 *
 * | abonnés | taux/vidéo, bon joueur (scores 88, Fid. 70, Mom. 1,3) |
 * |---|---|
 * | 1 000 | 1,832 % |
 * | 3 300 | 0,341 % |
 * | 10 000 | 0,297 % |
 * | 100 000 | **0,293 %** |
 * | 500 000 | **0,293 %** |
 *
 * À 0,293 % par vidéo, multiplier sa chaîne par 1 000 demande **2 365 vidéos** ; une partie en
 * offre quelques centaines. D'où le mur mesuré : **0 sortie de 1 k → 10 k sur 325 runs, 0
 * victoire sur 625**. `Découverte` joue bien le rôle de moteur au début — 300 vues offertes à
 * 1 000 abonnés — mais **s'effondre à 42 vues dès 10 000**, et rien ne prenait le relais.
 *
 * ── Ce que ce terme dit du jeu, et c'est le motif ─────────────────────────────
 *
 * Décision de Mathieu (16/08) : *« il faut que la Portée ou la conversion cessent d'être
 * plafonnées à valeur fixe et grandissent avec la chaîne »* — **« Exactement, on fait comme
 * ça. »** Une vraie chaîne ne grandit pas à taux constant : passé une certaine taille,
 * l'algorithme se met à la **recommander hors de son audience**, et cette poussée grandit avec
 * elle. C'est la « courbe façon MMORPG » réclamée depuis le début : ce n'est pas le joueur qui
 * devient meilleur, c'est la plateforme qui le porte.
 *
 * ── L'état neutre, par CONSTRUCTION et pas par vérification ⟦A-10⟧ ────────────
 *
 * **`SEUIL_RECOMMANDATION` vaut 3 300 parce que c'est là que se trouve l'ancre la plus haute**
 * (`ancres.test.ts` ne connaît que 150 et 3 300 abonnés) — exactement la justification de
 * `SEUIL_DEBUTANT = 150` pour le coup de pouce du débutant ⟦A-9⟧, à l'autre bout de la courbe.
 * En dessous et **à** 3 300, la fonction rend **exactement 1** : les 12 ancres ne peuvent pas
 * bouger d'une vue, et les profils qui ne franchissent jamais le seuil sortent des chiffres
 * identiques. Toute valeur plus basse déplacerait une ancre en silence.
 *
 * ── La forme, et pourquoi celle-là ────────────────────────────────────────────
 *
 * `1 + K × log10(A / SEUIL)` : une **décade d'abonnés gagne un palier de recommandation**, pas
 * un pourcentage composé. C'est la forme la plus lente qui décolle — une puissance de A ferait
 * exploser les vues, une constante ne changerait pas la nature du problème. Et parce qu'elle
 * **multiplie**, elle préserve l'écart entre les joueurs : un joueur moyen (0,062 %/vidéo)
 * reste arithmétiquement incapable d'atteindre le million, un bon joueur y arrive. C'est la
 * consigne : *« ne va pas jusqu'à rendre le jeu facile »*.
 */
export const SEUIL_RECOMMANDATION = 3300;
/**
 * Pente de la recommandation, **par décade d'abonnés** ✎.
 *
 * Lue à l'envers depuis la cible : le bon joueur doit passer de **0,293 %** à l'ordre de
 * **1,2 %** par vidéo dans les tranches hautes, soit ×4,3 à 500 000 abonnés — c'est-à-dire
 * `1 + K × log10(500 000 / 3 300) = 1 + K × 2,180 ≈ 4,3`, donc **K = 1,5**. Le segment
 * 3 300 → 1 000 000 demande alors ~810 vidéos au lieu de 2 365.
 */
export const PENTE_RECOMMANDATION = 1.5; // ✎
/**
 * `Recommandation(A) = 1` pour A ≤ 3 300 ; `1 + 1,5 × log10(A / 3 300)` au-delà (§4.5).
 * → 1,00 à 3 300 · 1,72 à 10 k · 3,22 à 100 k · 4,27 à 500 k · 4,72 à 1 M.
 *
 * ⚠️ ⟦A-35⟧ **Ce terme n'est plus appliqué par `computeVideoOutcome`.** Il est remplacé par
 * l'amplification algorithmique corrélée (§4.5 bis), pour deux raisons mesurées :
 *
 * 1. **Il ne débloquait rien** — 13 médianes de profil identiques au chiffre près (⟦A-28 §4⟧),
 *    parce qu'il s'allume à 3 300 et que la run médiane plafonne à ~3 000.
 * 2. **Il abîmait le réalisme du cas COURANT.** Une vidéo ordinaire fait 20 à 50 % du nombre
 *    d'abonnés ; avec ce multiplicateur, la vidéo médiane d'une chaîne à 1 M en faisait
 *    **226 %** (mesuré, `mesure-courbe.ts`). Il faisait monter la moyenne au lieu d'ouvrir la
 *    queue — or ce sont les **coups** qui font grandir une chaîne, pas les vidéos ordinaires.
 *
 * La fonction reste exportée : elle est encore lue par les tests de traçabilité et par
 * `docs/AVENANTS.md` ⟦A-28⟧. Elle ne pilote plus rien.
 */
export function recommandationAlgorithmique(abonnes) {
    if (abonnes <= SEUIL_RECOMMANDATION)
        return 1;
    return 1 + PENTE_RECOMMANDATION * Math.log10(abonnes / SEUIL_RECOMMANDATION);
}
// ─────────────────────────────────────────────────────────────────────────────
// §4.5 bis ⟦A-35⟧ L'amplification algorithmique — la portée externe, CORRÉLÉE
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦A-35⟧ **Ce que la plateforme fait d'une vidéo qui marche.**
 *
 * ── Le défaut, en une phrase ──────────────────────────────────────────────────
 *
 * `Découverte(A)` était un **cadeau plafonné à 300 vues qui s'effondre à 42 dès 10 000
 * abonnés** : le seul terme du moteur qui touchait des non-abonnés était une constante
 * décroissante. Conséquence arithmétique : `vues ≤ Découverte + 0,35 × A`, donc **une vidéo ne
 * pouvait JAMAIS dépasser ~35 % des abonnés** — à 1 M, 350 030 vues au maximum absolu.
 *
 * ── Ce que dit le métier, et c'est le motif ───────────────────────────────────
 *
 * Les chiffres **typiques** du jeu étaient justes : une vidéo ordinaire fait bien 20 à 50 % du
 * nombre d'abonnés. **Ce qui manquait, c'est la queue de distribution.** Aucune chaîne ne
 * grandit avec des vidéos ordinaires — elles grandissent par des **coups** :
 *
 * | taille de chaîne | ce que fait un succès (ordres de grandeur du métier) |
 * |---|---|
 * | 1 000 abonnés | **50 à 500 ×** les abonnés |
 * | 100 000 | **10 à 50 ×** |
 * | 1 000 000 | **5 à 20 ×** |
 *
 * Le jeu autorisait un maximum de **×8, avec 3 % de chance** (le jet viral). C'est là qu'était
 * le trou, et nulle part ailleurs : le budget de vidéos d'une partie (400 à 800 longues sur
 * 1 200 jours) est **du bon ordre** pour une vraie chaîne qui atteint le million en deux à
 * quatre ans. Ce qui manquait n'était pas du volume, c'était le **rendement des exceptionnelles**.
 *
 * ── La forme, et pourquoi celle-là ────────────────────────────────────────────
 *
 * Décision de Mathieu : *« Dans YouTube, t'es proposé à plein de gens qui ne sont pas des
 * abonnés. Ce 300 qui s'effondre, ça ne va pas — mais il faut qu'il soit corrélé à ce
 * pourcentage. »* **L'algorithme ne distribue pas au hasard : il amplifie ce qui marche déjà.**
 * Il regarde comment le public réagit — rétention, clic, part d'audience touchée — et c'est
 * cette réaction qui déclenche la diffusion aux inconnus.
 *
 * D'où un terme **multiplicatif** et non additif :
 *
 * ```
 * vues = (Découverte + A × Portée) × facteurs × Chance × Momentum × (1 + Amplification)
 * ```
 *
 * L'amplification est le produit de trois choses, et chacune répond à une exigence :
 *
 * - `signalInterne` — **la corrélation**. Qualité_eff (rétention) × Accroche_eff (le clic) ×
 *   Portée (la part du public qui a regardé), normalisés à ~1 pour une vidéo correcte. Élevé à
 *   `EXPOSANT_SIGNAL`, il rend l'amplification **fortement modulée par le métier** : une vidéo
 *   d'exécution faible en obtient ~0,12, une vidéo d'exécution forte ~2,6 — **un facteur 21**.
 * - `queueAlgorithmique` — **la queue lourde**. Trois vidéos sur quatre ne sont pas poussées du
 *   tout (l'état ordinaire reste l'état ordinaire) ; la quatrième l'est selon une loi de Pareto.
 * - `plafondVues` — **le réalisme du haut**. Le multiple maximal de l'audience **décroît** avec
 *   la taille (500 × à 1 k, 50 × à 100 k, 20 × à 1 M), ce qui est exactement ce que fait la
 *   plateforme : un petit peut exploser, un géant ne peut plus décupler.
 */
/** Part des vidéos que l'algorithme pousse hors de l'audience. Au-delà, l'amplification est **0**. */
export const SEUIL_POUSSEE = 0.25; // ✎
/**
 * Épaisseur de la queue (exposant de Pareto inverse) ✎. Plus il est grand, plus les coups sont
 * rares et gros. À 1,35 : 10 % des vidéos poussées dépassent ×3, 1 % dépasse ×26, 0,1 % dépasse
 * ×250 — avant modulation par le signal et avant le plafond.
 */
export const EXPOSANT_QUEUE = 1.35; // ✎
/**
 * Exposant du signal interne ✎. **C'est le garde-fou de compétence** : à 2, l'écart de signal
 * entre une exécution faible et une exécution forte (×4,6) devient un écart d'amplification de
 * **×21**. Un mauvais joueur n'atteint pas la queue, quel que soit son nombre de vidéos.
 */
export const EXPOSANT_SIGNAL = 2; // ✎
/** Valeurs de référence du signal — une vidéo « correcte » vaut 1 sur chacune. ✎ */
export const SIGNAL_QUALITE_REF = 0.85; // la Qualité_eff médiane mesurée sur 2 641 longues (§11.2)
export const SIGNAL_ACCROCHE_REF = 1.0;
export const SIGNAL_PORTEE_REF = 0.25;
/** Pivot de taille des lois de plafond : en dessous, le plafond ne bouge plus. ✎ */
export const A_PIVOT_AMPLI = 1000;
/** Multiple maximal de l'audience à `A_PIVOT_AMPLI` abonnés — le haut de la fourchette métier. ✎ */
export const MULTIPLE_PIVOT = 520;
/** Décroissance du multiple maximal par décade ✎ : 500 × à 1 k, ~96 × à 10 k, ~22 × à 1 M. */
export const DECROISSANCE_MULTIPLE = 0.45;
/**
 * Plancher d'audience du plafond de vues ✎ — sans lui, une chaîne à 0 abonné aurait un plafond
 * de 0 vue. 300 est la valeur de `Découverte` sous 1 000 abonnés : le plafond du démarrage est
 * donc « 500 fois ce que la plateforme te donne d'office ».
 */
export const A_PLANCHER_VUES = 300;
/**
 * `signalInterne` — ce que l'algorithme observe d'une vidéo, normalisé à ~1 pour une vidéo
 * correcte. C'est la **corrélation** exigée : la diffusion externe est une fonction de la
 * performance interne, jamais une constante.
 */
export function signalInterne(qualiteEff, accrocheEff, portee) {
    return ((Math.max(0, qualiteEff) / SIGNAL_QUALITE_REF) *
        (Math.max(0, accrocheEff) / SIGNAL_ACCROCHE_REF) *
        (Math.max(0, portee) / SIGNAL_PORTEE_REF));
}
/**
 * Borne basse du tirage ✎. `u = 0` signifierait une poussée infinie : c'est le cas dégénéré du
 * jet viral (`chance = 8`), et il vaut le **maximum**, borné par `plafondVues`.
 */
export const U_MIN_POUSSEE = 1e-6;
/**
 * Taille de chaîne à partir de laquelle la plateforme pousse à pleine puissance ✎.
 *
 * **1 000, et ce n'est pas un seuil de calibrage : c'est le Grand Saut** ⟦A-1⟧. Une chaîne sans
 * historique n'a rien que l'algorithme puisse mesurer — c'est le cliché exact du métier, « les
 * mille premiers sont les plus durs », et le jeu le dit déjà ailleurs (l'argent commence à
 * 1 000). En dessous, la poussée monte **linéairement** : à 100 abonnés, l'algorithme n'accorde
 * qu'un dixième de ce qu'il accorderait à 1 000. Rien ne se déclenche d'un coup.
 */
export const A_ELIGIBLE_POUSSEE = 1000;
/** `min(1 ; A / 1 000)` — la montée en éligibilité de la chaîne à la recommandation. */
export function rampeEligibilite(abonnes) {
    return Math.min(1, Math.max(0, abonnes) / A_ELIGIBLE_POUSSEE);
}
/**
 * `queueAlgorithmique(u)` — la loterie de la plateforme, `u` uniforme sur ]0;1].
 *
 * `u ≥ SEUIL_POUSSEE` ⇒ **0** : la vidéo n'est pas poussée, et c'est le cas des trois quarts.
 * En dessous, loi de Pareto `(SEUIL/u)^EXPOSANT − 1`, **continue en `u = SEUIL`** (elle y vaut
 * exactement 0 : pas de marche dans la distribution — la loi n'a aucun palier).
 */
export function queueAlgorithmique(u) {
    const v = Math.max(U_MIN_POUSSEE, u);
    if (v >= SEUIL_POUSSEE)
        return 0;
    return Math.pow(SEUIL_POUSSEE / v, EXPOSANT_QUEUE) - 1;
}
/** Multiple maximal de l'audience qu'une vidéo peut atteindre à `A` abonnés (décroît avec `A`). */
export function multipleMaximal(abonnes) {
    const a = Math.max(abonnes, A_PIVOT_AMPLI);
    return MULTIPLE_PIVOT * Math.pow(A_PIVOT_AMPLI / a, DECROISSANCE_MULTIPLE);
}
/**
 * Plafond dur de vues d'une vidéo. **C'est lui qui remplace le jet viral ×8 comme borne haute** :
 * une vidéo qui cumule une chance de 1,6, un jet viral et une amplification maximale ne dépasse
 * pas ce que la plateforme sait faire pour une chaîne de cette taille.
 */
export function plafondVues(abonnes) {
    return Math.max(abonnes, A_PLANCHER_VUES) * multipleMaximal(abonnes);
}
/**
 * Valeur du tirage quand `amplification_roll` est **absent** : `u = 1`, donc **aucune poussée**.
 *
 * ⟦A-10⟧ **L'état neutre par défaut**, et il n'est pas décoratif. `computeVideoOutcome` est
 * appelée par trois chemins : `publish` (qui tire, seedé), la preview du client (qui ne peut pas
 * connaître la chance à venir) et les tests. Le défaut « pas de poussée » signifie que **la
 * vidéo ordinaire est le cas par défaut** — la queue ne s'ajoute que si quelqu'un la tire
 * explicitement, jamais par effet de bord.
 */
export const U_SANS_POUSSEE = 1;
/**
 * `Portée = clamp(0,10 + 0,20 × (Fidélité/100) + 0,05 × (Momentum − 0,8)/0,7 ; 0,10 ; 0,35)`
 * (§4.5) — monte avec la Fidélité et le Momentum.
 */
export function portee(fidelite, momentum, bonus = 0) {
    const brute = 0.1 + 0.2 * (fidelite / 100) + 0.05 * ((momentum - 0.8) / 0.7);
    // ⟦A-17⟧ Le bonus s'ajoute APRÈS le clamp de la formule de base, et le total est borné à
    // `PLAFOND_PORTEE_TOTAL`. `bonus = 0` par défaut ⇒ comportement d'avant, au point près.
    return clamp(clamp(brute, PLANCHER_PORTEE, PLAFOND_PORTEE) + bonus, PLANCHER_PORTEE, PLAFOND_PORTEE_TOTAL);
}
// ─────────────────────────────────────────────────────────────────────────────
// §4.6 TauxConversion
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⟦A-9⟧ **Le coup de pouce du débutant** — ce qui fait que « 100 abonnés » arrive quand le
 * programme de l'arc 1 se termine, et pas 100 vidéos plus tard.
 *
 * **Le problème, chiffré.** Une vidéo correcte d'une chaîne à zéro abonné rapporte **0,5
 * abonné pour toute sa courbe** : 300 vues de Découverte × Accroche à froid × une Qualité
 * de débutant, le tout converti à 0,8 %. À ce rythme, 100 abonnés demandent **~108 vidéos**
 * (mesuré, joueur correct N(70,8)) là où Mathieu en veut **8 à 10**. Ce n'est pas un réglage
 * qui manque, c'est un terme : **la formule n'a aucune ancre sous 150 abonnés**, et rien n'y
 * décrit le démarrage.
 *
 * **La forme choisie, et pourquoi ici.** `BonusPetiteChaîne` est déjà, par sa propre
 * définition, « ce qui reproduit l'ancre +1 abonné au démarrage » : c'est donc là que le
 * démarrage se décrit. Le bonus part de `BONUS_DEBUTANT` à 0 abonné et **redescend
 * linéairement jusqu'à 3 pile à `SEUIL_DEBUTANT`**, c'est-à-dire exactement la valeur qu'il
 * avait déjà. Conséquence directe et voulue : **à 150 abonnés et au-delà, la fonction est
 * identique au caractère près à ce qu'elle était** — les six ancres de calibrage (150 et
 * 3 300 abonnés) ne bougent pas d'une vue. C'est l'état neutre par défaut qu'exige ⟦A-10⟧.
 *
 * ⚠️ **Ce terme est une PROPOSITION de la session moteur, pas une décision.** A-9 dit « la
 * courbe d'abonnés est calibrée pour arriver à 100 quand le programme se termine » sans dire
 * par quel levier ; c'est le levier le moins invasif que j'aie trouvé. L'alternative franche
 * serait de **déplacer l'ancre A1** (« 150 abonnés, vidéo bien faite : +1 abonné »), qui est
 * arithmétiquement incompatible avec « 11 abonnés par vidéo sous 100 ». Depuis ⟦A-10⟧ c'est
 * possible — mais ça n'appartient qu'à Mathieu.
 */
export const BONUS_DEBUTANT = 80; // ✎
/**
 * Abonnés au-dessus desquels le coup de pouce a totalement disparu (✎).
 *
 * **150, parce que c'est là que se trouve l'ancre A1.** Toute valeur plus haute déplacerait
 * une ancre en silence ; toute valeur plus basse laisserait une marche dans la courbe.
 */
export const SEUIL_DEBUTANT = 150;
/**
 * ⟦A-35⟧ **Le prix d'un abonné, en points.** Décision de Mathieu : *« le prix de l'abonné,
 * 476 points, je pense que c'est aussi quelque chose qui doit évoluer »*.
 *
 * ── Le défaut ─────────────────────────────────────────────────────────────────
 *
 * `BonusPetiteChaîne` faisait **exactement l'inverse de ce qu'il fallait** : il valait 3 jusqu'à
 * 1 000 abonnés, tombait à 1 en 2 000 abonnés — **le prix d'un abonné TRIPLAIT dans la bande
 * 1 k → 3 k** — puis ne bougeait plus JAMAIS. C'est la moitié du mur : un joueur payait sa
 * marche la plus dure au moment où sa `Découverte` s'effondrait, et n'était jamais récompensé
 * de grandir ensuite.
 *
 * ── La forme ──────────────────────────────────────────────────────────────────
 *
 * Une **loi de puissance unique**, continue, sans palier ni marche :
 * `3 × (1 + (A − 150)/A_DEMI_PRIX)^(−EXPOSANT_PRIX)`.
 *
 * | abonnés | multiplicateur | prix d'un abonné (exéc. forte, Fid. 50) |
 * |---|---|---|
 * | 150 | 3,00 | ~88 points |
 * | 1 000 | 2,58 | ~102 |
 * | 3 300 | 2,13 | ~123 |
 * | 10 000 | 1,72 | ~153 |
 * | 100 000 | 1,05 | ~251 |
 * | 1 000 000 | 0,63 | ~416 |
 *
 * **La marche monte, et elle monte pour toujours** — c'est la sensation MMORPG demandée : au
 * début quelques milliers de points valent une fortune, plus haut la même performance relative
 * rapporte proportionnellement moins, mais en valeur absolue beaucoup plus. Et surtout la
 * hausse est **étalée sur toute la courbe** au lieu d'être un mur de ×3 planté à 1 000 abonnés.
 *
 * ⚠️ Le rapport, pas les valeurs : le prix monte de ×4 de 1 k à 1 M, quand l'amplification
 * ouvre bien davantage. **C'est cet écart-là qui est la croissance** — deux courbes qui montent
 * au même rythme rendraient exactement le jeu d'avant, avec des nombres plus gros.
 *
 * ⟦A-9⟧ **La rampe du débutant sous 150 abonnés est inchangée au caractère près** : elle décrit
 * l'onboarding (« 100 abonnés quand le programme de l'arc 1 se termine »), pas la courbe.
 */
export const A_DEMI_PRIX = 850; // ✎
export const EXPOSANT_PRIX = 0.15; // ✎
/** Valeur de la loi de taille à `SEUIL_DEBUTANT` — le socle sur lequel le coup de pouce s'ajoute. */
export const BONUS_TAILLE = 3;
/**
 * `LoiDeTaille(A)` — le multiplicateur **de taille de chaîne** seul, sans le coup de pouce
 * ⟦A-9⟧ : `3 × (1 + (A − 150)/850)^(−0,15)`. Strictement décroissant, plat sous 150 abonnés.
 */
export function loiDeTaille(abonnes) {
    return (BONUS_TAILLE *
        Math.pow(1 + Math.max(0, abonnes - SEUIL_DEBUTANT) / A_DEMI_PRIX, -EXPOSANT_PRIX));
}
/**
 * ⟦A-48⟧ **La fin du programme de l'arc 1** — 100 abonnés, la cible d'⟦A-9⟧ (« 100 abonnés
 * quand le programme se termine », atteinte : 10 vidéos, J8).
 *
 * C'est le point de jonction : **en dessous, la rampe d'⟦A-9⟧ est intacte au caractère près**,
 * donc l'onboarding ne peut pas bouger d'une vidéo. Au-dessus, l'école prend le relais.
 */
export const A_FIN_PROGRAMME = 100;
/**
 * ⟦A-48⟧ **Échelle du fondu de l'école** ✎ — la distance en abonnés sur laquelle le coup de
 * pouce du débutant s'éteint une fois le programme terminé.
 *
 * ⟦A-10⟧ **`0` est l'état neutre** : le fondu redevient la rampe linéaire d'⟦A-9⟧, qui meurt à
 * 150 abonnés, c'est-à-dire **exactement la loi d'avant, au caractère près**. C'est le
 * commutateur qui permet de prouver qu'une dérive mesurée vient bien d'ici.
 */
export const A_ECOLE = 1000; // ✎
/**
 * ⟦A-48⟧ **Raideur du fondu** ✎ — plus il est grand, plus l'école se referme vite.
 *
 * Il est **lu à l'envers depuis la contrainte du haut** : le fondu doit être **éteint à 10 000
 * abonnés** (×1,03 sur le multiplicateur), sans quoi il déplacerait la tranche 1 k → 1 M et,
 * pire, le mur arithmétique du joueur faible ⟦A-22⟧, qui vit à ~12 600 abonnés ⟦A-35 §5⟧.
 * 2,5 est la valeur la plus plate qui tient cette contrainte.
 */
export const EXPOSANT_ECOLE = 2.5; // ✎
/**
 * ⟦A-48⟧ **L'école du jeu, et pourquoi elle remplace une falaise.**
 *
 * ── Le défaut, mesuré ─────────────────────────────────────────────────────────
 *
 * ⟦A-35⟧ a calibré la courbe **de 1 000 abonnés au million** — 314 vidéos. Personne n'avait
 * mesuré la tranche **100 → 1 000**, et c'est là que se joue la partie : **502 vidéos sur les
 * ~650 d'une partie entière, soit 75 %** (campagne 100 parties, `simulate --runs 4`) :
 *
 * | tranche | vidéos (méd.) |
 * |---|---|
 * | 0 → 100 | 10 |
 * | 100 → 200 | 10 |
 * | **200 → 500** | **207** |
 * | **500 → 1 000** | **285** |
 * | 1 k → 10 k | 162 |
 *
 * La cause est arithmétique, et ce **n'est pas** `Découverte` : sous 1 000 abonnés `Découverte`
 * ne s'effondre pas du tout, elle vaut **300, constante**. C'est justement le problème — elle
 * représente **93 % des vues offertes à 100 abonnés et encore 58 % à 1 000**, et *une constante
 * ne produit aucune croissance*. Le seul terme qui variait dans la bande était
 * `bonusPetiteChaîne`, et il faisait le contraire de ce qu'il fallait : la rampe d'⟦A-9⟧
 * tombait de **28,7 à 100 abonnés à 3,0 à 150** — **le prix d'un abonné était multiplié par 9,7
 * en cinquante abonnés** — puis la loi de taille restait plate (3,00 → 2,70 sur 850 abonnés).
 * **Une falaise, puis un plateau de 800 abonnés où la vidéo ordinaire rapporte 1,5 abonné.**
 *
 * ── La forme, et pourquoi celle-là ────────────────────────────────────────────
 *
 * Le coup de pouce ne s'arrête plus net : il **fond**, en loi de puissance, sur toute l'école
 * du jeu ⟦A-1⟧ (la tranche sans argent, 0 → 1 000). Trois propriétés, et chacune est une
 * contrainte du projet, pas une préférence :
 *
 * 1. **`A ≤ 100` est inchangé au caractère près** — la rampe d'⟦A-9⟧ y domine par construction
 *    (`max`), et le fondu la raccorde **exactement** à sa valeur de jonction. La cible de
 *    Mathieu « 100 abonnés en 10 vidéos, au jour 8 » ne peut pas bouger : ce n'est pas une
 *    mesure, c'est de l'algèbre.
 * 2. **Le fondu est éteint à 10 000 abonnés** (×1,03) : la tranche haute et le mur du joueur
 *    faible ⟦A-22⟧ ne sont pas touchés. Vérifié à part : à exécution faible, le taux net par
 *    vidéo reste **négatif de 3 000 à 50 000 abonnés** avant comme après — le mur est intact.
 * 3. **Le prix d'un abonné devient strictement croissant sur toute la courbe**, sans marche —
 *    ce qu'⟦A-35⟧ avait obtenu au-dessus de 1 000 et que la falaise détruisait en dessous :
 *
 * | abonnés | 100 | 150 | 200 | 500 | 1 000 | 3 300 | 10 000 | 100 000 |
 * |---|---|---|---|---|---|---|---|---|
 * | AVANT | 28,67 | **3,00** | 2,97 | 2,85 | 2,70 | 2,38 | 2,05 | 1,47 |
 * | **APRÈS** | **28,67** | **25,72** | **23,20** | **13,92** | **7,86** | **3,09** | **2,12** | **1,47** |
 * | × | ×1,00 | ×8,6 | ×7,8 | ×4,9 | ×2,9 | ×1,30 | **×1,03** | **×1,00** |
 */
export function coupDePouceDebutant(abonnes) {
    const a = Math.max(0, abonnes);
    const rampeA9 = Math.max(0, 1 - a / SEUIL_DEBUTANT);
    // ⟦A-10⟧ L'état neutre : sans école, la rampe d'⟦A-9⟧ seule — la loi d'avant, au caractère près.
    if (A_ECOLE <= 0 || a <= A_FIN_PROGRAMME)
        return rampeA9;
    const jonction = 1 - A_FIN_PROGRAMME / SEUIL_DEBUTANT;
    return jonction * Math.pow(1 + (a - A_FIN_PROGRAMME) / A_ECOLE, -EXPOSANT_ECOLE);
}
export function bonusPetiteChaine(abonnes) {
    return loiDeTaille(abonnes) + (BONUS_DEBUTANT - BONUS_TAILLE) * coupDePouceDebutant(abonnes);
}
// ─────────────────────────────────────────────────────────────────────────────
// ⟦C46 · A-168 §1⟧ Le rythme de la partie
// ─────────────────────────────────────────────────────────────────────────────
/**
 * **Le rythme de la partie** : l'unique facteur global de sa longueur.
 *
 * Matt (30/09/2026) : *« la simulation est trop longue »*, précisé : la PARTIE entière, il faut
 * trop de vidéos et de jours pour arriver au million (148 vidéos jouées à la main de 1 k à 1 M,
 * 800 jours médians). Plutôt que cinquante retouches de courbe, **un seul nombre**, lu dans
 * `content/meta.json → rythme_croissance` (validé par Zod, `]0 ; 10]`) : chaque vidéo réussie
 * compte pour `k` vidéos (voir `rythmeDeLaVideo`, qui dit à quelles vidéos, et où).
 *
 * **Absent ⇔ 1 ⇔ le jeu d'avant, au caractère près** (⟦A-10⟧, l'état neutre par défaut). Une
 * valeur non finie ou non positive retombe sur 1 : un contenu corrompu ne doit jamais éteindre
 * la croissance.
 */
export function rythmeCroissance(content) {
    const r = content.meta?.rythme_croissance;
    return typeof r === "number" && Number.isFinite(r) && r > 0 ? r : 1;
}
/** Sous ce nombre d'abonnés le rythme ne joue pas : c'est l'école du jeu ⟦A-1⟧, calibrée à part ⟦A-9 · A-48⟧. */
export const A_DEBUT_RYTHME = 1000;
/** Passé ce nombre d'abonnés le rythme joue à plein. Entre les deux, il monte linéairement. */
export const A_PLEIN_RYTHME = 2000;
/**
 * Sous ce score d'exécution, la vidéo n'est pas poussée plus vite : le rythme ne lui donne rien.
 * **55**, parce que c'est juste au-dessus de ce que fait un joueur faible (mesuré sur 10 518
 * longues : p90 = 51,1 ; moyenne 70 ; forte 86).
 */
export const EXECUTION_DEBUT_RYTHME = 55;
/** À partir de ce score d'exécution le rythme joue à plein (75 : le haut d'un joueur moyen). */
export const EXECUTION_PLEIN_RYTHME = 75;
/**
 * ⟦C46 · A-168 §1⟧ **Combien de vidéos vaut CETTE vidéo.** Multiplie ses VUES (donc, d'un seul
 * geste, ses abonnés, ses Clicks et son XP : le tempo de toute la progression, pas une seule
 * jauge) :
 *
 * ```
 * 1 + (rythme − 1) × rampe_abonnés(A) × lissage(rampe_exécution(score))
 * ```
 *
 * Deux garde-fous, et ce sont eux qui font de ce facteur un réglage de LONGUEUR et pas de
 * DIFFICULTÉ :
 *
 * 1. **Il ne joue qu'à partir de 1 000 abonnés** (rampe linéaire jusqu'à 2 000). L'école du jeu
 *    ⟦A-1⟧ (« 100 abonnés en 10 vidéos, J8 », ⟦A-9 · A-48⟧) est **intacte au caractère près** :
 *    mesuré, les paliers 100 / 200 / 500 / 1 000 tombent aux mêmes jours, aux mêmes vidéos.
 * 2. **Il ne récompense que l'exécution.** Un facteur plat ×6 laisse passer le joueur faible :
 *    mesuré, 2 victoires sur 16 (au lieu de 0), parce que sa croissance, lente mais positive,
 *    n'est retenue que par l'horizon. La plateforme ne pousse pas plus vite une vidéo bâclée :
 *    le poids passe de 0 à 1 entre une exécution de 55 et une exécution de 75 (courbe lissée),
 *    donc le faible (≈ 45) n'en tire rien, le moyen (≈ 70) presque tout, le fort (≈ 86) tout.
 *    C'est la corrélation compétence → réussite ⟦A-22⟧, tenue par construction.
 *
 * `rythme = 1` rend **exactement 1** à toute taille et à tout score (l'état neutre, sans arrondi).
 */
export function rythmeDeLaVideo(content, abonnes, execution) {
    const k = rythmeCroissance(content);
    if (k === 1)
        return 1;
    const ta = clamp((abonnes - A_DEBUT_RYTHME) / (A_PLEIN_RYTHME - A_DEBUT_RYTHME), 0, 1);
    const te = clamp((execution - EXECUTION_DEBUT_RYTHME) / (EXECUTION_PLEIN_RYTHME - EXECUTION_DEBUT_RYTHME), 0, 1);
    return 1 + (k - 1) * ta * (te * te * (3 - 2 * te));
}
/** Le prix d'un abonné **en points**, pour l'affichage (§4.6) : l'inverse du TauxConversion. */
export function prixAbonne(tauxConversion) {
    return tauxConversion > 0 ? 1 / tauxConversion : Infinity;
}
/**
 * `MalusNulles = 1 − 0,02 × max(0 ; nulles_visibles − 3)`, **plancher 0** (§4.6, PLAN §4.6).
 *
 * **Arbitrage A36** (trouvé par `simulate.ts`, M1.19) : la spec ne borne pas ce malus. Au-delà
 * de **53 nulles visibles** il devient négatif, donc le TauxConversion aussi — et **chaque vue
 * versée RETIRE des abonnés**. Ce n'est pas un équilibrage sévère, c'est une absurdité : une
 * chaîne pleine de flops ne convertit personne, elle ne dé-convertit pas les gens qui la
 * regardent. Le plancher 0 est le minimum nécessaire pour que la formule garde un sens.
 *
 * Le joueur n'est pas piégé pour autant : l'action `menage` sort une nulle du compte (§6.5).
 * Faut-il un plancher plus haut (0,2 ?) pour éviter le mur à 53 ? C'est une question
 * d'équilibrage, pas de correction — à trancher avec la campagne M3.12.
 */
/**
 * Plancher du malus de nulles. ✎ valeur d'équilibrage (M3.12) — mais son **existence** est un
 * correctif, pas un réglage.
 *
 * ⚠️ Sans lui, `malusNulles` atteint **exactement 0** à 53 nulles visibles : le taux de
 * conversion devient nul et la chaîne ne peut plus JAMAIS convertir personne, quoi que le
 * joueur fasse ensuite. Mesuré en campagne (« Conversion éteinte », 10 profils sur 25) et en
 * playtest : la progression décélère au lieu d'accélérer — 50 abonnés en 29 vidéos, 100 en 93.
 * **Publier davantage rendait objectivement plus faible**, c'est-à-dire que le jeu punissait
 * exactement ce qu'il demande de faire.
 *
 * Le ménage (§6.5) reste la vraie sortie : passer ses nulles en privé les retire du compte.
 * Le plancher garantit seulement qu'une chaîne négligée est **handicapée, jamais morte**.
 */
export const PLANCHER_NULLES = 0.25;
export function malusNulles(nullesVisibles) {
    return Math.max(PLANCHER_NULLES, 1 - 0.02 * Math.max(0, nullesVisibles - 3));
}
/**
 * `TC = [0,001 + 0,005 × (0,5·qNorm + 0,5·fNorm)] × BonusPetiteChaîne × MalusNulles` (§4.6).
 * Figé dans le `VideoRecord` à la publication.
 */
export function tauxConversion(qualiteEff, fidelite, abonnes, nullesVisibles) {
    const qNorm = clamp((qualiteEff - PLANCHER_QUALITE) / 1.7, 0, 1);
    const fNorm = fidelite / 100;
    const base = 0.001 + 0.005 * (0.5 * qNorm + 0.5 * fNorm);
    return base * bonusPetiteChaine(abonnes) * malusNulles(nullesVisibles);
}
// ─────────────────────────────────────────────────────────────────────────────
// §12 Le helper pur — chance injectée
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Formule seule, chance injectée — utilisé par les ancres de calibrage (§12), par
 * `publish` (M1.9) et par la preview client non autoritative (PLAN §6).
 *
 * Ne lit rien, n'écrit rien, ne tire rien : le jet de chance est un paramètre.
 */
export function computeVideoOutcome(p) {
    const { scores, format } = p;
    // — Scores d'étape (§4.2) —
    const execution_score = executionScore(scores, format);
    const s_qual = scoreQualite(scores);
    const s_acc = scoreAccroche(scores, format);
    const s_diff = scores.promotion ?? 0;
    // — Potentiel d'Accroche : table §4.4 + modificateur d'alignement du sujet —
    const modAlignement = p.alignment_band === "chaos"
        ? ACCROCHE_BONUS_CHAOS
        : p.alignment_band === "lumiere"
            ? ACCROCHE_MALUS_LUMIERE
            : 0;
    const potAccroche = clamp(p.accroche_potentiel + modAlignement, PLANCHER_ACCROCHE, PLAFOND_ACCROCHE);
    // — Facteurs effectifs (§4.3) —
    // ⟦C4⟧ `mult_accroche` : cible `accroche` d'un `multiplier` de contenu — c'est par là que
    // passe la **fenêtre d'opportunité ×3** de PLAN §4.8 (`fenetre_panne_reseau`). Neutre à 1.
    const accroche_eff = capture(PLANCHER_ACCROCHE, potAccroche, s_acc) * (p.mult_accroche ?? 1);
    let qualite_eff = capture(PLANCHER_QUALITE, clamp(p.qualite_potentiel, PLANCHER_QUALITE, PLAFOND_QUALITE), s_qual);
    // Modificateurs APRÈS capture, cumulables (§4.3).
    if (p.crunch)
        qualite_eff *= MULT_CRUNCH;
    if (p.fatigue)
        qualite_eff *= MULT_FATIGUE;
    // ⟦C4⟧ Les `multiplier` de contenu de cible `qualite` — SPEC-CONTENU §2.1 : ils visent le
    // facteur **EFFECTIF**, donc ici, après capture et après crunch/fatigue. Neutres à 1.
    qualite_eff *= p.mult_qualite ?? 1;
    // Short : Diffusion = 1,0, il n'y a pas d'étape Promotion (PLAN §4.4).
    const diffusion_eff = (format === "short"
        ? 1.0
        : capture(PLANCHER_DIFFUSION, clamp(p.diffusion_potentiel, PLANCHER_DIFFUSION, PLAFOND_DIFFUSION), s_diff)) * (p.mult_diffusion ?? 1); // ⟦C4⟧ cible `diffusion` — neutre à 1
    // — VuesPic (§4.1, §4.5, §4.5 bis) —
    const porteeEff = portee(p.loyalty, p.momentum, p.portee_potentiel ?? 0);
    const base = decouverte(p.subscribers) + p.subscribers * porteeEff;
    // ⟦A-35⟧ **L'amplification algorithmique** — la portée externe, corrélée à la performance
    //   interne. `signal` est ce que la plateforme observe (rétention × clic × part d'audience
    //   touchée) ; `queue` est sa loterie, tirée du jet de chance (aucun nouveau `purpose`).
    //   `amplification_roll` permet d'injecter le tirage (tests, preview) ; absent, il est
    //   **dérivé de `chance`**, donc une même vidéo rend toujours le même résultat.
    const signal = signalInterne(qualite_eff, accroche_eff, porteeEff);
    const u = p.amplification_roll ?? U_SANS_POUSSEE;
    const amplification = queueAlgorithmique(u) * Math.pow(signal, EXPOSANT_SIGNAL) * rampeEligibilite(p.subscribers);
    const vues_ordinaires = base * accroche_eff * qualite_eff * diffusion_eff * p.chance * p.momentum;
    let vues = vues_ordinaires * (1 + amplification);
    if (p.alignment_band === "chaos")
        vues *= MULT_VUES_CHAOS;
    if (p.window_boost)
        vues *= MULT_VUES_WINDOW;
    // Le plafond dur : le multiple maximal de l'audience DÉCROÎT avec la taille — c'est ce que
    // fait la plateforme, et c'est ce qui empêche un jet viral cumulé à une amplification
    // maximale de produire un chiffre absurde. ⚠️ Il ne descend **jamais** sous les vues non
    // amplifiées : c'est un plafond de POUSSÉE, pas un plafond de vues.
    vues = Math.min(vues, Math.max(vues_ordinaires, plafondVues(p.subscribers)));
    const vues_pic = Math.round(vues);
    // — TauxConversion et abonnés (§4.6) —
    //
    // ⚠️ Le **poids volatil** (§4.6, PLAN §4.4 : short OU chaos, ET Fidélité < 50) entre dans
    // `tc`, PAS dans `subs_bruts` — audit des formules, B3. Il était appliqué après coup sur
    // `subs_bruts`, donc **il n'atteignait jamais le versement** : le pipeline fige
    // `taux_conversion` dans le `VideoRecord`, et les deux versements (`curves.verserTranche`
    // et `sleep.verserRente`) recalculent `vues × video.taux_conversion`. La règle était morte
    // côté moteur, et la preview du client — qui lit ce helper — annonçait la MOITIÉ de ce que
    // le moteur créditait réellement. Dans `tc`, elle se fige comme tous les autres facteurs
    // (§4.1) et les deux chemins disent enfin la même chose.
    const volatil = (format === "short" || p.alignment_band === "chaos") && p.loyalty < 50;
    const tc = tauxConversion(qualite_eff, p.loyalty, p.subscribers, p.nulles_visibles) *
        (volatil ? MULT_POIDS_VOLATIL : 1);
    const subs_bruts = vues_pic * tc;
    return {
        vues_pic,
        tc,
        subs_bruts,
        // Au dernier versement de la courbe initiale, le report ≥ 0,5 monte à +1 : le total
        // observé vaut donc round(bruts) — c'est ce que testent les ancres (§4.6).
        subs_arrondis: Math.round(subs_bruts),
        accroche_eff,
        qualite_eff,
        diffusion_eff,
        execution_score,
    };
}
//# sourceMappingURL=formula.js.map